/**
 * pageBuilderUtils.ts
 * Shared constants and helpers for the Make a Page block builder.
 */
import {
  defaultHero,
  defaultWhatsIncluded,
  defaultSpacesSection,
  defaultApplication,
  defaultRulesCTA,
  defaultMission,
  defaultMeetMakers,
  defaultFAQ,
  defaultContact,
} from "@/data/siteContent";
import type { PageBlock, BlockType } from "@/utils/customPageStore";

export const BLOCK_TYPES: { type: BlockType; icon: string; label: string; desc: string }[] = [
  { type: "heading", icon: "ri-heading", label: "Heading", desc: "A large section title" },
  { type: "text", icon: "ri-text", label: "Text Block", desc: "Paragraph of body text" },
  { type: "image", icon: "ri-image-line", label: "Image", desc: "Full-width image with optional caption" },
  { type: "image-grid", icon: "ri-grid-line", label: "Image Grid", desc: "Multiple images in a grid layout" },
  { type: "image-detail", icon: "ri-image-2-line", label: "Image + Details", desc: "Image with title & description" },
  { type: "two-col", icon: "ri-layout-column-line", label: "Two Columns", desc: "Text or image on either side" },
  { type: "cta", icon: "ri-cursor-line", label: "Call to Action", desc: "Headline + button" },
  { type: "divider", icon: "ri-separator", label: "Divider", desc: "Horizontal rule / spacer" },
  { type: "section", icon: "ri-layout-line", label: "Section", desc: "A full-width section with layout and content" },
];

export function newBlockId(): string {
  return `block-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export function cloneBlock(block: PageBlock): PageBlock {
  return {
    ...block,
    id: newBlockId(),
    sectionMeta: block.sectionMeta
      ? { ...block.sectionMeta, data: { ...block.sectionMeta.data } }
      : undefined,
    images: block.images
      ? block.images.map((img) => ({
          ...img,
          id: `img-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`,
        }))
      : undefined,
  };
}

export function newBlock(type: BlockType): PageBlock {
  const id = newBlockId();
  switch (type) {
    case "heading":
      return { id, type, content: "New Section Heading", headingLevel: "h2" };
    case "text":
      return { id, type, content: "Write your content here. Click to edit this text block.", textStyle: "default" };
    case "image":
      return { id, type, content: "", imageAlt: "Image", imageSize: "full", showCaption: true };
    case "image-grid":
      return {
        id, type, content: "",
        images: [
          { id: `img-${Date.now()}-a`, url: "", alt: "" },
          { id: `img-${Date.now()}-b`, url: "", alt: "" },
        ],
        imageCols: 2,
      };
    case "image-detail":
      return {
        id, type, content: "",
        imageAlt: "Image",
        subContent: "Title for this image",
        ctaLabel: "Description text goes here.",
      };
    case "two-col":
      return {
        id, type,
        content: "Left column content goes here.",
        subContent: "Right column content goes here.",
        leftType: "text",
        rightType: "text",
      };
    case "cta":
      return {
        id, type,
        content: "Ready to get started?",
        subContent: "Join us today and be part of something special.",
        ctaLabel: "Get Started",
        ctaUrl: "/",
      };
    case "divider":
      return { id, type, content: "" };
  }
}

export interface HomeSectionTemplate {
  id: string;
  label: string;
  icon: string;
  description: string;
  blocks: PageBlock[];
}

export function homeSectionTemplates(): HomeSectionTemplate[] {
  return [
    {
      id: "hero",
      label: "Hero Section",
      icon: "ri-home-smile-line",
      description: "Full-width background image with headline, tagline, body, CTA buttons, and stats row",
      blocks: [{ id: newBlockId(), type: "section", content: "Hero Section", sectionMeta: { layout: "hero", data: { ...defaultHero } } }],
    },
    {
      id: "whats-included",
      label: "What's Included",
      icon: "ri-gift-line",
      description: "Pink background with eyebrow, heading, and icon cards in a grid",
      blocks: [{ id: newBlockId(), type: "section", content: "What's Included", sectionMeta: { layout: "info-cards", data: { ...defaultWhatsIncluded } } }],
    },
    {
      id: "spaces",
      label: "Space Options",
      icon: "ri-layout-grid-line",
      description: "Pink background with heading and image cards with prices and features",
      blocks: [{ id: newBlockId(), type: "section", content: "Space Options", sectionMeta: { layout: "space-grid", data: { ...defaultSpacesSection } } }],
    },
    {
      id: "application",
      label: "Application Section",
      icon: "ri-file-list-line",
      description: "Two-column layout with gradient quote card, photos, info cards, and heading",
      blocks: [{ id: newBlockId(), type: "section", content: "Application Section", sectionMeta: { layout: "gradient-quote", data: { ...defaultApplication } } }],
    },
    {
      id: "rules",
      label: "Rules & Guidelines",
      icon: "ri-shield-check-line",
      description: "CTA banner with heading, subtext, and two buttons",
      blocks: [{ id: newBlockId(), type: "section", content: "Rules & Guidelines", sectionMeta: { layout: "cta-banner", data: { ...defaultRulesCTA } } }],
    },
    {
      id: "mission",
      label: "Our Mission",
      icon: "ri-heart-line",
      description: "Two-column layout with founder photo, mission statement, story, and pillars",
      blocks: [{ id: newBlockId(), type: "section", content: "Our Mission", sectionMeta: { layout: "founder-story", data: { ...defaultMission } } }],
    },
    {
      id: "makers",
      label: "Meet Our Makers",
      icon: "ri-group-line",
      description: "Artist gallery heading and subheading",
      blocks: [{ id: newBlockId(), type: "section", content: "Meet Our Makers", sectionMeta: { layout: "artist-heading", data: { ...defaultMeetMakers } } }],
    },
    {
      id: "faq",
      label: "FAQ Section",
      icon: "ri-question-line",
      description: "Gradient sidebar with heading, accordion Q&A, and stats",
      blocks: [{ id: newBlockId(), type: "section", content: "FAQ Section", sectionMeta: { layout: "faq-accordion", data: { ...defaultFAQ } } }],
    },
    {
      id: "contact",
      label: "Contact Section",
      icon: "ri-mail-line",
      description: "Contact info cards, social links, heading, and intro text",
      blocks: [{ id: newBlockId(), type: "section", content: "Contact Section", sectionMeta: { layout: "contact-info", data: { ...defaultContact } } }],
    },
  ];
}